import type { IDeposit } from "@/modules/deposit/common/types";
import { Amount } from "@/shared/components";
import { CollapseItem } from "@ab.uitools/ui-library/components/Collapse";
import { Copy } from "@ab.uitools/ui-library/components/Copy";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Tooltip } from "@ab.uitools/ui-library/components/Tooltip";
import { Positions } from "@ab.uitools/ui-library/components/Tooltip/types";
import { type RefObject, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  deposit?: IDeposit;
  scrollbarContainerRef?: RefObject<HTMLDivElement | null>;
}

export const DepositDetailRows = ({ deposit, scrollbarContainerRef }: Props) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const toggleCollapse = () => setIsOpen(!isOpen);
  const getTranslationKey = (bool: boolean) => {
    return bool ? "general.yes" : "general.no";
  };

  return (
    <div>
      <div>
        <div>
          <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
            <Text type="secondary" size="medium" weight="semibold">
              {t("general.contractNumber")}
            </Text>

            <div className="flexbox align-items--center">
              <Copy
                textAfterCopy={t("general.copied")}
                size="medium"
                parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                text={deposit?.contractNumber as string}
              />
              <Text size="medium" weight="bold">{deposit?.contractNumber}</Text>
            </div>
          </div>
          <Divider
            color="light"
            type="primary"
            isHorizontal
          />
        </div>
        <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
          <Text type="secondary" size="medium" weight="semibold">
            {t("deposits.contractAmount")}
          </Text>
          <Amount
            currency={deposit?.currency}
            amount={deposit?.initialAmount as number}
          />
        </div>
        <Divider
          color="light"
          type="primary"
          isHorizontal
        />
      </div>
      <div>
        <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
          <Text type="secondary" size="medium" weight="semibold">
            {t("deposits.nominalInterestRate")}
          </Text>
          <Text size="medium" weight="bold">
            {deposit?.nominalInterestRate}%
          </Text>
        </div>
        <Divider
          color="light"
          type="primary"
          isHorizontal
        />
      </div>
      <div>
        <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
          <Text type="secondary" size="medium" weight="semibold">
            {t("deposits.investmentDate")}
          </Text>
          <Text size="medium" weight="bold">
            {deposit?.creationDate}
          </Text>
        </div>
        <Divider
          color="light"
          type="primary"
          isHorizontal
        />
      </div>
      <div>
        <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
          <Text type="secondary" size="medium" weight="semibold">
            {t("deposits.maturityDate")}
          </Text>

          <Text size="medium" weight="bold">
            {deposit?.maturityDate}
          </Text>
        </div>
        <Divider
          color="light"
          type="primary"
          isHorizontal
        />
      </div>
      <div>
        <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
          <Text type="secondary" size="medium" weight="semibold">
            {t("deposits.calculatedInterestAmount")}
          </Text>
          <Amount
            currency={deposit?.currency}
            amount={deposit?.calculatedInterestAmount as number}
          />
        </div>
        <Divider
          color="light"
          type="primary"
          isHorizontal
        />
      </div>
      <CollapseItem
        className="detail-collapse"
        isOpen={isOpen}
        toggle={toggleCollapse}
        title={t("general.additionalDetails")}
      >
        <div>
          <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
            <Text type="secondary" size="medium" weight="semibold">
              {t("deposits.depositType")}
            </Text>
            <Tooltip
              position={Positions.TOP_CENTER}
              id="description-tooltip"
              text={deposit?.description as string}
            />
            <Text
              className="text-nowrap truncate ml-16 cursor-pointer"
              size="medium"
              id="description-tooltip"
              weight="bold"
            >
              {deposit?.description}
            </Text>
          </div>
          <Divider
            color="light"
            type="primary"
            isHorizontal
          />
        </div>
        <div>
          <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
            <Text type="secondary" size="medium" weight="semibold">
              {t("deposits.replenishable")}
            </Text>
            <Text size="medium" weight="bold">
              {t(getTranslationKey(!!deposit?.isReplenishable))}
            </Text>
          </div>
          <Divider
            color="light"
            type="primary"
            isHorizontal
          />
        </div>
        <div>
          <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
            <Text type="secondary" size="medium" weight="semibold">
              {t("deposits.extendable")}
            </Text>
            <Text size="medium" weight="bold">
              {t(getTranslationKey(!!deposit?.isExtendable))}
            </Text>
          </div>
          <Divider
            color="light"
            type="primary"
            isHorizontal
          />
        </div>
        <div>
          <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
            <Text type="secondary" size="medium" weight="semibold">
              {t("deposits.capitalizable")}
            </Text>
            <Text size="medium" weight="bold">
              {t(getTranslationKey(!!deposit?.isCapitalizable))}
            </Text>
          </div>
          <Divider
            color="light"
            type="primary"
            isHorizontal
          />
        </div>
        <div>
          <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
            <Text type="secondary" size="medium" weight="semibold">
              {t("deposits.depositInterestPaymentFrequency")}
            </Text>
            <Text size="medium" weight="bold">
              {t(`deposits.${deposit?.depositInterestPaymentFrequency}`)}
            </Text>
          </div>
          <Divider
            color="light"
            type="primary"
            isHorizontal
          />
        </div>
        <div>
          <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
            <Text type="secondary" size="medium" weight="semibold">
              {t("deposits.depositReceiptAccount")}
            </Text>
            <Text size="medium" weight="bold">
              {deposit?.depositReceiptAccount}
            </Text>
          </div>
          <Divider
            color="light"
            type="primary"
            isHorizontal
          />
        </div>
        {!!deposit?.interestReceiptAccount && (
          <div>
            <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
              <Text type="secondary" size="medium" weight="semibold">
                {t("deposits.interestReceiptAccount")}
              </Text>
              <Text size="medium" weight="bold">
                {deposit?.interestReceiptAccount}
              </Text>
            </div>
            <Divider
              color="light"
              type="primary"
              isHorizontal
            />
          </div>
        )}
        <div>
          <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
            <Text type="secondary" size="medium" weight="semibold">
              {t("deposits.withdrawable")}
            </Text>
            <Text size="medium" weight="bold">
              {t(getTranslationKey(!!deposit?.isWithdrawable))}
            </Text>
          </div>
          <Divider
            color="light"
            type="primary"
            isHorizontal
          />
        </div>
        <div>
          <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
            <Text type="secondary" size="medium" weight="semibold">
              {t("deposits.earlyTerminable")}
            </Text>
            <Text size="medium" weight="bold">
              {t(getTranslationKey(!!deposit?.isEarlyTerminable))}
            </Text>
          </div>
        </div>
      </CollapseItem>
      <Divider
        color="light"
        type="primary"
        isHorizontal
      />
    </div>
  );
};
